import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus,
  Building,
  Phone,
  Mail,
  Clock,
  LayoutGrid,
  CalendarDays,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import SiteServiceFormDialog from "@/components/SiteServiceFormDialog";
import SiteServiceDetailDialog from "@/components/SiteServiceDetailDialog";
import SiteServiceCalendar from "@/components/SiteServiceCalendar";
import ExportButtons from "@/components/ExportButtons";

/**
 * /site-services — pest control, fire safety, HVAC, bin pick-ups, sharpening
 * services and on-call trades. Cards are grouped by kind; each shows a live
 * next-due chip driven by (last_visit_date or first_visit_date) + interval.
 * Managers get the "Add" and "Log visit" actions plus PDF-report viewing.
 */

const chipTone = (nextDue) => {
  if (!nextDue) return { bg: "#EEF2F7", fg: "#607087", label: "On call-out" };
  const today = new Date().toISOString().slice(0, 10);
  const diff = Math.floor(
    (new Date(nextDue) - new Date(today)) / (1000 * 60 * 60 * 24),
  );
  if (diff < 0)
    return { bg: "#FCE4E4", fg: "#8A1F1F", label: `Overdue by ${-diff}d` };
  if (diff <= 7)
    return { bg: "#FFE9C2", fg: "#7A4A00", label: `Due in ${diff}d` };
  return { bg: "#E6F4EA", fg: "#1F6F3E", label: `Due in ${diff}d` };
};

const SiteServices = () => {
  const { isManager } = useAuth();
  const [providers, setProviders] = useState([]);
  const [meta, setMeta] = useState({ kinds: [], intervals: [] });
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("cards");
  const [editing, setEditing] = useState(null); // provider being edited (or {} for new)
  const [detail, setDetail] = useState(null); // provider whose detail dialog is open

  const load = async () => {
    setBusy(true);
    try {
      const [m, p] = await Promise.all([
        api.get("/site-services/meta"),
        api.get("/site-services/providers"),
      ]);
      setMeta(m.data);
      setProviders(p.data);
    } catch {
      toast.error("Could not load site services");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const list = filter === "all" ? providers : providers.filter((p) => p.kind === filter);
    const out = {};
    for (const p of list) {
      const k = p.kind || "Other";
      (out[k] = out[k] || []).push(p);
    }
    // Sort kinds by seed-order so the page always renders the same way.
    const order = meta.kinds || [];
    return Object.entries(out).sort(
      (a, b) => order.indexOf(a[0]) - order.indexOf(b[0]),
    );
  }, [providers, filter, meta.kinds]);

  const kindCounts = useMemo(() => {
    const m = {};
    for (const p of providers) m[p.kind] = (m[p.kind] || 0) + 1;
    return m;
  }, [providers]);

  return (
    <>
      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <div className="label-eyebrow">Facility</div>
            <h1 className="font-display text-3xl sm:text-4xl">Site Services</h1>
            <p className="text-sm text-[#607087] max-w-xl mt-1">
              Recurring site contracts and on-call trades. Log every visit and
              attach the compliance PDF — the next-due date auto-computes from
              the last visit + interval.
            </p>
          </div>
          {isManager && (
            <div className="flex items-center gap-2 flex-wrap">
              <ExportButtons
                entity="site-services"
                rows={providers}
                testidPrefix="site-services-export"
              />
              <button
                data-testid="site-services-add-btn"
                onClick={() => setEditing({})}
                className="nb-btn nb-btn-dark px-4 py-2 flex items-center gap-2"
              >
                <Plus size={16} strokeWidth={2.5} /> Add provider
              </button>
            </div>
          )}
        </div>

        {/* View toggle: Cards | Calendar */}
        <div className="flex items-center gap-1.5 mb-3" data-testid="site-services-view-toggle">
          <button
            data-testid="site-services-view-cards"
            onClick={() => setView("cards")}
            className={`nb-btn px-3 py-1.5 text-xs flex items-center gap-1 ${
              view === "cards" ? "nb-btn-dark" : "bg-white"
            }`}
          >
            <LayoutGrid size={12} strokeWidth={2.5} /> Cards
          </button>
          <button
            data-testid="site-services-view-calendar"
            onClick={() => setView("calendar")}
            className={`nb-btn px-3 py-1.5 text-xs flex items-center gap-1 ${
              view === "calendar" ? "nb-btn-dark" : "bg-white"
            }`}
          >
            <CalendarDays size={12} strokeWidth={2.5} /> Calendar
          </button>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-1.5 mb-5" data-testid="site-services-filter">
          <button
            data-testid="site-services-filter-all"
            onClick={() => setFilter("all")}
            className={`nb-btn px-3 py-1.5 text-xs ${filter === "all" ? "nb-btn-dark" : "bg-white"}`}
          >
            All <span className="opacity-70 ml-1">({providers.length})</span>
          </button>
          {(meta.kinds || []).map((k) => {
            const n = kindCounts[k] || 0;
            if (!n && filter !== k) return null;
            return (
              <button
                key={k}
                data-testid={`site-services-filter-${k.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setFilter(k)}
                className={`nb-btn px-3 py-1.5 text-xs ${filter === k ? "nb-btn-dark" : "bg-white"}`}
              >
                {k} <span className="opacity-70 ml-1">({n})</span>
              </button>
            );
          })}
        </div>

        {/* Grouped provider cards */}
        {busy && <div className="text-[#607087]">Loading…</div>}
        {!busy && view === "calendar" && (
          <SiteServiceCalendar
            providers={providers}
            kindFilter={filter}
            onLogVisit={(p) => setDetail(p)}
          />
        )}
        {!busy && view === "cards" && providers.length === 0 && (
          <div className="nb-card p-10 text-center text-[#607087]">
            <Building className="mx-auto mb-3 text-[#607087]" size={36} strokeWidth={1.5} />
            No site-service providers yet.
            {isManager && (
              <div className="mt-2 text-sm">
                Tap <b>Add provider</b> to add Pest Control, Fire Safety, HVAC and friends.
              </div>
            )}
          </div>
        )}
        {view === "cards" && grouped.map(([kind, items]) => (
          <section key={kind} className="mb-6" data-testid={`site-services-group-${kind}`}>
            <div className="label-eyebrow mb-2">
              {kind} <span className="opacity-60">· {items.length}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {items.map((p) => {
                const chip = chipTone(p.next_due_date);
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`site-services-card-${p.id}`}
                    onClick={() => setDetail(p)}
                    className="nb-card p-4 text-left hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-display text-lg truncate">{p.name}</div>
                        <div className="label-eyebrow mt-0.5">
                          {p.interval} · {p.kind}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {p.auto_log && (
                            <span
                              data-testid={`site-services-auto-${p.id}`}
                              className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#0A3A7A] text-white"
                            >
                              AUTO-LOG
                            </span>
                          )}
                          {p.missed && (
                            <span
                              data-testid={`site-services-missed-${p.id}`}
                              title={p.missed_reason || "Last visit marked incomplete"}
                              className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#E5263F] text-white"
                            >
                              MISSED {p.last_visit_date}
                            </span>
                          )}
                        </div>
                      </div>
                      <span
                        className="text-[10px] font-bold px-2 py-1 rounded border-2 border-black whitespace-nowrap"
                        style={{ background: chip.bg, color: chip.fg }}
                      >
                        {chip.label}
                      </span>
                    </div>
                    <div className="mt-3 text-xs text-[#607087] flex flex-col gap-0.5">
                      {p.contact_name && (
                        <div>
                          <b>Contact:</b> {p.contact_name}
                        </div>
                      )}
                      {p.phone && (
                        <div className="flex items-center gap-1">
                          <Phone size={11} strokeWidth={2.5} /> {p.phone}
                        </div>
                      )}
                      {p.email && (
                        <div className="flex items-center gap-1 truncate">
                          <Mail size={11} strokeWidth={2.5} /> {p.email}
                        </div>
                      )}
                      <div className="flex items-center gap-1 mt-1">
                        <Clock size={11} strokeWidth={2.5} />
                        Last visit:{" "}
                        <b>{p.last_visit_date || "never"}</b>
                      </div>
                      {p.cost_per_visit != null && (
                        <div>
                          <b>${Number(p.cost_per_visit).toFixed(2)}</b> per visit
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <SiteServiceFormDialog
        open={editing !== null}
        provider={editing?.id ? editing : null}
        meta={meta}
        onMetaChange={async () => {
          try {
            const { data } = await api.get("/site-services/meta");
            setMeta(data);
          } catch {
            /* silent */
          }
        }}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
      <SiteServiceDetailDialog
        open={!!detail}
        provider={detail}
        onClose={() => setDetail(null)}
        onEditProvider={(p) => {
          setDetail(null);
          setEditing(p);
        }}
        onChanged={load}
      />
    </>
  );
};

export default SiteServices;
